import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { SyntheticEvent, useContext, useMemo, useState } from "react";
import { LeadTag } from "@/modules/lead-tags/dtos";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import useAddLeadTag from "@/modules/lead-page/hooks/add-lead-tag.hook";
import { userHasPermission } from "@/modules/common/utils";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
const AddLeadTag = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [selectedTags, setSelectedTags] = useState<LeadTag[]>([]);
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const canManageRestricted = userHasPermission(
    {
      name: PermissionsEnum.MANAGE_RESTRICTED_LEAD_TAGS,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions
  );

  const { addLeadTagMutation, filteredTags, lead, isLoading } =
    useAddLeadTag(setOpen);

  const visibleTags = useMemo(
    () =>
      canManageRestricted
        ? filteredTags
        : filteredTags.filter((tag: LeadTag) => !tag.isRestricted),
    [filteredTags, canManageRestricted]
  );

  const handleChange = (_event: SyntheticEvent, value: LeadTag[]) => {
    setSelectedTags(value);
  };

  const handleAddLeadTag = () => {
    addLeadTagMutation({
      leadUId: lead?.data?.lead?.uid || "",
      tagUIds: selectedTags.map((tag) => tag.uid),
    });
  };

  return (
    <Box>
      <ResponsiveEditDialog
        open={open}
        setOpen={setOpen}
        header="Add Tags"
        width={500}
        handleConfirm={handleAddLeadTag}
        disabledConfirmButton={selectedTags?.length === 0}
        confirmText="Add"
        cancelText="Cancel"
        isLoading={isLoading}
      >
        <Autocomplete
          multiple
          options={visibleTags}
          value={selectedTags}
          getOptionLabel={(option) => option.name}
          filterSelectedOptions
          onChange={handleChange}
          noOptionsText="No lead tags to add"
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Select Tags" />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip label={option.name} {...getTagProps({ index })} />
            ))
          }
        />
      </ResponsiveEditDialog>
    </Box>
  );
};

export default AddLeadTag;
